import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';
import DashboardLayout from '../components/DashboardLayout';
import { FiLogOut, FiUser, FiShield, FiBell, FiGlobe, FiCopy, FiCheck, FiTrash2, FiKey } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import toast from 'react-hot-toast';

const SettingsPage = () => {
  const { isAuthenticated, loading: authLoading, logout, walletAddress } = useAuth();
  const { profile, loading: profileLoading } = useProfile();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('account');
  const [copied, setCopied] = useState(false);
  const [notifications, setNotifications] = useState({
    email: true,
    profileViews: true,
    newConnections: true,
    tips: true,
    marketing: false,
  });

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, authLoading, navigate]);

  const handleCopyWallet = async () => {
    if (walletAddress) {
      await navigator.clipboard.writeText(walletAddress);
      setCopied(true);
      toast.success('Wallet address copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleNotificationChange = (key) => {
    setNotifications(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
    toast.success('Notification preferences updated');
  };

  const handleDeleteAccount = () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      toast.error('Account deletion not yet implemented');
      // TODO: Implement account deletion
    }
  };

  if (authLoading || profileLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lime-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  const tabs = [
    { id: 'account', name: 'Account', icon: FiUser },
    { id: 'security', name: 'Security', icon: FiShield },
    { id: 'notifications', name: 'Notifications', icon: FiBell },
    { id: 'privacy', name: 'Privacy', icon: FiGlobe },
  ];

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white">Settings</h1>
          <p className="text-gray-400 mt-1">Manage your account and preferences</p>
        </div>

        {/* Tabs */}
        <div className="border-b border-dark-700 mb-8">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors flex items-center ${
                  activeTab === tab.id
                    ? 'border-lime-500 text-lime-400'
                    : 'border-transparent text-gray-400 hover:text-white hover:border-gray-600'
                }`}
              >
                <SafeIcon icon={tab.icon} className="mr-2" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="glass-dark rounded-xl border border-dark-700 p-6">
          {/* Account Tab */}
          {activeTab === 'account' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-lime-bright mb-4 flex items-center">
                  <SafeIcon icon={FiUser} className="mr-2" />
                  Account Information
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-dark-200 mb-2">
                      Username
                    </label>
                    <div className="glass p-3 rounded-lg border border-dark-700">
                      <p className="text-white">@{profile?.username || 'Not set'}</p>
                    </div>
                    <p className="text-xs text-dark-400 mt-1">
                      Your unique HubID username
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-dark-200 mb-2">
                      Display Name
                    </label>
                    <div className="glass p-3 rounded-lg border border-dark-700">
                      <p className="text-white">{profile?.display_name || 'Not set'}</p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-dark-200 mb-2">
                      Connected Wallet
                    </label>
                    <div className="flex items-center space-x-3">
                      <div className="flex-1 glass p-3 rounded-lg border border-dark-700">
                        <p className="text-white font-mono text-sm">
                          {walletAddress || 'Not connected'}
                        </p>
                      </div>
                      {walletAddress && (
                        <button
                          onClick={handleCopyWallet}
                          className="glass-lime text-lime-400 px-4 py-3 rounded-lg font-medium hover:text-lime-300 transition-colors border border-lime-500/50 inline-flex items-center"
                        >
                          <SafeIcon icon={copied ? FiCheck : FiCopy} className="mr-2" />
                          {copied ? 'Copied!' : 'Copy'}
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-dark-400 mt-1">
                      Your wallet address is used for authentication and payments
                    </p>
                  </div>

                  <div className="pt-6 border-t border-dark-700">
                    <Link
                      to="/profile"
                      className="glass-lime text-lime-400 px-4 py-2 rounded-lg font-medium hover:text-lime-300 transition-colors border border-lime-500/50 inline-flex items-center"
                    >
                      Edit Profile
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-lime-bright mb-4 flex items-center">
                  <SafeIcon icon={FiShield} className="mr-2" />
                  Security Settings
                </h3>
                
                <div className="space-y-4">
                  <div className="glass p-4 rounded-lg border border-dark-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-white mb-1">Wallet Authentication</h4>
                        <p className="text-sm text-dark-300">
                          Your account is secured with Web3 wallet authentication
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-lime-500/20 rounded-lg flex items-center justify-center border border-lime-500/50">
                        <SafeIcon icon={FiKey} className="text-lime-400 text-xl" />
                      </div>
                    </div>
                  </div>

                  <div className="glass p-4 rounded-lg border border-dark-700">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-white mb-1">Two-Factor Authentication</h4>
                        <p className="text-sm text-dark-300">
                          Add an extra layer of security to your account
                        </p>
                      </div>
                      <button className="glass text-white px-4 py-2 rounded-lg font-medium hover:text-lime-400 transition-colors border border-dark-700 text-sm">
                        Coming Soon
                      </button>
                    </div>
                  </div>

                  <div className="pt-6 border-t border-dark-700">
                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                      <h4 className="font-medium text-red-400 mb-2 flex items-center">
                        <SafeIcon icon={FiTrash2} className="mr-2" />
                        Danger Zone
                      </h4>
                      <p className="text-sm text-dark-300 mb-4">
                        Once you delete your account, there is no going back. Please be certain.
                      </p>
                      <button
                        onClick={handleDeleteAccount}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors text-sm"
                      >
                        Delete Account
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-lime-bright mb-4 flex items-center">
                  <SafeIcon icon={FiBell} className="mr-2" />
                  Notification Preferences
                </h3>
                
                <div className="space-y-4">
                  {Object.entries(notifications).map(([key, value]) => (
                    <div key={key} className="glass p-4 rounded-lg border border-dark-700 flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-white mb-1 capitalize">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </h4>
                        <p className="text-sm text-dark-300">
                          {key === 'email' && 'Receive email notifications'}
                          {key === 'profileViews' && 'Get notified when someone views your profile'}
                          {key === 'newConnections' && 'Be notified of new connection requests'}
                          {key === 'tips' && 'Get notified when you receive tips'}
                          {key === 'marketing' && 'Receive updates about new features and promotions'}
                        </p>
                      </div>
                      <button
                        onClick={() => handleNotificationChange(key)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          value ? 'bg-lime-500' : 'bg-dark-700'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            value ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Privacy Tab */}
          {activeTab === 'privacy' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-lime-bright mb-4 flex items-center">
                  <SafeIcon icon={FiGlobe} className="mr-2" />
                  Privacy Settings
                </h3>
                
                <div className="space-y-4">
                  <div className="glass p-4 rounded-lg border border-dark-700">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-white">Public Profile</h4>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        profile?.is_public 
                          ? 'bg-lime-500/20 text-lime-400 border border-lime-500/50' 
                          : 'bg-dark-700 text-dark-300 border border-dark-600'
                      }`}>
                        {profile?.is_public ? 'Public' : 'Private'}
                      </span>
                    </div>
                    <p className="text-sm text-dark-300 mb-4">
                      {profile?.is_public 
                        ? 'Your profile is visible to everyone'
                        : 'Your profile is only visible to you'}
                    </p>
                    <Link
                      to="/profile"
                      className="text-lime-400 hover:text-lime-300 text-sm font-medium"
                    >
                      Change in Profile Settings →
                    </Link>
                  </div>

                  <div className="glass p-4 rounded-lg border border-dark-700">
                    <h4 className="font-medium text-white mb-2">Data & Analytics</h4>
                    <p className="text-sm text-dark-300 mb-4">
                      We collect minimal data to provide our services. Your wallet address and profile data are stored securely.
                    </p>
                    <div className="space-y-2">
                      <button className="glass text-white px-4 py-2 rounded-lg font-medium hover:text-lime-400 transition-colors border border-dark-700 text-sm w-full text-left">
                        Download My Data
                      </button>
                      <button className="glass text-white px-4 py-2 rounded-lg font-medium hover:text-lime-400 transition-colors border border-dark-700 text-sm w-full text-left">
                        View Privacy Policy
                      </button>
                    </div>
                  </div>

                  <div className="glass p-4 rounded-lg border border-dark-700">
                    <h4 className="font-medium text-white mb-2">IPFS Publishing</h4>
                    <p className="text-sm text-dark-300 mb-4">
                      Your profile can be published to IPFS for permanent, decentralized storage. This is optional.
                    </p>
                    <p className="text-xs text-dark-400">
                      Published profiles are immutable and cannot be deleted once on IPFS.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Logout Button */}
          <div className="pt-6 mt-6 border-t border-dark-700">
            <button
              onClick={logout}
              className="glass text-white px-4 py-2 rounded-lg font-medium hover:text-red-400 transition-colors border border-dark-700 hover:border-red-500/50 inline-flex items-center"
            >
              <SafeIcon icon={FiLogOut} className="mr-2" />
              Logout
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SettingsPage;
